import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  TvIcon,
  GlobeAltIcon,
  TagIcon,
  CalendarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { usePlatforms, useCreatePlatform, useUpdatePlatform, useDeletePlatform } from '../hooks/usePlatforms';
import CreatePlatformModal, { CreatePlatformData } from '../components/platforms/CreatePlatformModal';
import EditPlatformModal, { EditPlatformData } from '../components/platforms/EditPlatformModal';
import DeleteConfirmationModal from '../components/platforms/DeleteConfirmationModal';
import { toast } from 'react-hot-toast';
import type { Platform } from '../services/platformService';

const Platforms: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [platformToEdit, setPlatformToEdit] = useState<Platform | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [platformToDelete, setPlatformToDelete] = useState<Platform | null>(null);

  // API hooks
  const { data: platformsResponse, isLoading, error, refetch } = usePlatforms({
    search: searchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    limit: 50
  });

  const createPlatformMutation = useCreatePlatform();
  const updatePlatformMutation = useUpdatePlatform();
  const deletePlatformMutation = useDeletePlatform();

  const platforms = platformsResponse?.platforms || [];

  const getClientTypeIcon = (clientType: string) => {
    switch (clientType) {
      case 'web_app':
        return <ComputerDesktopIcon className="h-4 w-4" />;
      case 'mobile_web':
        return <GlobeAltIcon className="h-4 w-4" />;
      case 'mobile_app':
        return <DevicePhoneMobileIcon className="h-4 w-4" />;
      case 'tv_app':
        return <TvIcon className="h-4 w-4" />;
      default:
        return <DeviceTabletIcon className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'inactive':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'draft':
        return 'Taslak';
      case 'inactive':
        return 'Pasif';
      default:
        return status;
    }
  };

  const handleCreatePlatform = async (data: CreatePlatformData) => {
    try {
      await createPlatformMutation.mutateAsync(data);
      toast.success('Platform başarıyla oluşturuldu!');
      setShowCreateModal(false);
    } catch (error) {
      console.error('Platform oluşturma hatası:', error);
      toast.error('Platform oluşturulurken bir hata oluştu');
    }
  };

  const handleEditPlatform = (platform: Platform) => {
    setPlatformToEdit(platform);
    setShowEditModal(true);
  };

  const handleUpdatePlatform = async (data: EditPlatformData) => {
    if (!platformToEdit) return;
    
    try {
      await updatePlatformMutation.mutateAsync({ 
        id: platformToEdit.id, 
        data 
      });
      toast.success('Platform başarıyla güncellendi!');
      setShowEditModal(false);
      setPlatformToEdit(null);
    } catch (error) {
      console.error('Platform güncelleme hatası:', error);
      toast.error('Platform güncellenirken bir hata oluştu');
    }
  };

  const handleDeleteClick = (platform: Platform) => {
    setPlatformToDelete(platform);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!platformToDelete) return;
    
    try {
      await deletePlatformMutation.mutateAsync(platformToDelete.id);
      toast.success('Platform başarıyla silindi!');
      setShowDeleteModal(false);
      setPlatformToDelete(null);
    } catch (error) {
      console.error('Platform silme hatası:', error);
      toast.error('Platform silinirken bir hata oluştu');
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platformlar</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Platform ve istemci uygulamalarınızı yönetin
            </p>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-md p-4 dark:bg-red-900 dark:border-red-700">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Veriler yüklenemedi
              </h3>
              <div className="mt-2">
                <button
                  onClick={() => refetch()}
                  className="bg-red-100 px-3 py-1 rounded-md text-sm text-red-800 hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700"
                >
                  Tekrar Dene
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platformlar</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Platform ve istemci uygulamalarınızı yönetin
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={createPlatformMutation.isPending}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {createPlatformMutation.isPending ? 'Oluşturuluyor...' : 'Yeni Platform'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Platform ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div className="w-full sm:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="draft">Taslak</option>
            <option value="inactive">Pasif</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse"
            >
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-gray-300 rounded dark:bg-gray-600" />
                    <div className="ml-3">
                      <div className="h-4 w-32 bg-gray-300 rounded dark:bg-gray-600" />
                      <div className="h-3 w-16 bg-gray-300 rounded mt-2 dark:bg-gray-600" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 h-12 bg-gray-300 rounded dark:bg-gray-600" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Platforms Grid */}
      {!isLoading && platforms.length > 0 && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {platforms.map((platform: Platform) => (
            <div
              key={platform.id}
              className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DevicePhoneMobileIcon className="h-8 w-8 text-blue-500" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {platform.name}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(platform.status)}`}>
                        {getStatusText(platform.status)}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Link
                      to={`/platforms/${platform.id}`}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
                      title="Görüntüle"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => handleEditPlatform(platform)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
                      title="Düzenle"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(platform)}
                      disabled={deletePlatformMutation.isPending}
                      className="p-2 text-gray-400 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400 disabled:opacity-50"
                      title="Sil"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {platform.description && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {platform.description}
                  </p>
                )}

                {/* Tags */}
                {platform.tags && platform.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {platform.tags.map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Client Summary */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {platform.clientCount || 0} İstemci
                    </span>
                    <div className="flex space-x-1">
                      {/* Client icons placeholder */}
                      <div className="text-gray-400 dark:text-gray-500">
                        {getClientTypeIcon('web_app')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <CalendarIcon className="h-3 w-3 mr-1" />
                  Oluşturuldu: {new Date(platform.createdAt).toLocaleDateString('tr-TR')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && platforms.length === 0 && (
        <div className="text-center py-12">
          <DevicePhoneMobileIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            Platform bulunamadı
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' 
              ? 'Arama kriterlerinize uygun platform bulunamadı.' 
              : 'Henüz hiç platform oluşturulmamış.'}
          </p>
          {(!searchTerm && statusFilter === 'all') && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                İlk Platformunuzu Oluşturun
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Platform Modal */}
      <CreatePlatformModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreatePlatform}
      />

      {/* Edit Platform Modal */}
      <EditPlatformModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setPlatformToEdit(null);
        }}
        onSave={handleUpdatePlatform}
        platform={platformToEdit}
        isLoading={updatePlatformMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setPlatformToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Platform Sil"
        message="Bu platformu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm bağlı istemciler ve konfigürasyonlar da silinecektir."
        itemName={platformToDelete?.name || ''}
        isLoading={deletePlatformMutation.isPending}
        requiresConfirmation={true}
        dangerKeyword="DELETE"
      />
    </div>
  );
};

export default Platforms; 